import {Injectable, Type} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {FollowApiModel} from '../../../../../api/models/follow.api-model';
import {QueryParamsApiModel} from '../../../../../api/models/query-params-api.model';
import {ResultListApiModel} from '../../../../../api/models/result-list.api.model';
import {FollowedSubjectInterface, FollowerListItemInterface} from '../../../../../core/interfaces/follower-item.interface';
import {UserRestService} from '../../../../../api/services/user.rest.service';
import {SocialListAdapter, SocialListLoadResult} from '../social-list-adapter.interface';
import {SocialListModalConfig} from '../social-list-modal-config.interface';
import {SocialListType} from '../social-list-type.type';
import {FollowListItemComponent} from '../items/follow-list-item.component';
import {FollowListItemsBuilderService} from '../builders/follow-list-items-builder.service';

type UserFollowsSubject = FollowedSubjectInterface;

@Injectable({providedIn: 'root'})
export class UserFollowsSocialListAdapter implements SocialListAdapter<UserFollowsSubject, never, FollowerListItemInterface> {
  public readonly type: SocialListType = 'user-follows';
  public readonly itemComponent: Type<FollowListItemComponent> = FollowListItemComponent;
  public readonly emptyTranslationKey = 'shared.modal.follow_list.empty';
  public readonly trimmedTranslationKey = 'shared.modal.follow_list.trimmed';
  public readonly titleTranslationKey = 'shared.modal.modal_service.follows.title';

  constructor(
    private readonly userRS: UserRestService,
    private readonly followListItemsBuilder: FollowListItemsBuilderService,
  ) {
  }

  public loadPage(
    config: SocialListModalConfig<UserFollowsSubject>,
    page: number,
    pageSize: number
  ): Observable<SocialListLoadResult<FollowerListItemInterface>> {
    const initialFollows = config.initialData || [];
    if (initialFollows.length) {
      const start = (page - 1) * pageSize;
      const records = initialFollows.slice(start, start + pageSize);

      return this.followListItemsBuilder.buildItems$(records, config.subject.apiModel).pipe(
        map((items) => ({
          items,
          hasMore: start + records.length < initialFollows.length
        }))
      );
    }

    const params = new QueryParamsApiModel();
    params.page = page;
    params.limit = pageSize;
    params.pagination = true;

    return this.userRS.getAggregatedReceivedFollowsSubResourceCollection(config.subject['@id'], params).pipe(
      map((resultListAM: ResultListApiModel<FollowApiModel>) => resultListAM?.records || []),
      mergeMap((followAMs) => this.followListItemsBuilder.buildItems$(followAMs, config.subject.apiModel).pipe(
        map((items) => ({items, hasMore: followAMs.length >= pageSize}))
      )),
      catchError(() => of({items: [], hasMore: false}))
    );
  }

  public mergeItems(existingItems: FollowerListItemInterface[], incomingItems: FollowerListItemInterface[]): FollowerListItemInterface[] {
    return this.followListItemsBuilder.mergeItems(existingItems, incomingItems);
  }

  public trackBy(index: number, item: FollowerListItemInterface): string {
    return item?.follower?.['@id'] || `${index}`;
  }
}
